import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { keyBy } from 'lodash-es';
import omit from 'lodash-es/omit';
import { IBlobFile, ICompletedUpload, IUploadFile } from '.';
import { uniqPrimativeMerge } from '../../utils/arrayUtils';
import {
  IBlobReference,
  IBlobRenameAction,
  IBlobRenameRequest,
  ISalesDocumentPermissionUpdate,
  IUploadFileError
} from './filesInterfaces';
import * as thunks from './thunks';

export interface IFileState {
  fileHash: { [index: string]: IBlobFile };
  salesDocumentsUris: string[];
  uploads: IUploadFiles;
  loading: string[];
}

interface IUploadFiles {
  active: IUploadFile[];
  completed: ICompletedUpload[];
  failed: IUploadFile[];
  progress: { [index: string]: number };
}

const initialState: IFileState = {
  fileHash: {},
  loading: [],
  salesDocumentsUris: [],
  uploads: {
    active: [],
    completed: [],
    failed: [],
    progress: {}
  }
};

// tslint:disable: no-empty
const filesSlice = createSlice({
  initialState,
  name: 'files',
  reducers: {
    createFolder: (state, payload: PayloadAction<IBlobFile>) => {},
    deleteSalesDocument: {
      prepare: (uri: string) => ({ payload: { uri } }),
      reducer: (state, action: PayloadAction<{ uri: string }>) => {}
    },
    fetchFiles: (state, action: PayloadAction<IBlobReference>) => {
      if (action.payload.reset) {
        const matching = Object.keys(state.fileHash).filter(x =>
          x.includes(`${action.payload.container}/${action.payload.folder}`)
        );
        state.fileHash = omit(state.fileHash, matching);
      }
    },
    fetchSalesDocuments: {
      prepare: (prefix?: string, hideSpinner?: boolean) => ({ payload: { prefix, hideSpinner } }),
      reducer: (state, action: PayloadAction<{ prefix?: string; hideSpinner?: boolean }>) => {}
    },
    processFileRenameActions: (state, action: PayloadAction<IBlobRenameAction[]>) => {
      const salesDocumentsUris = [...state.salesDocumentsUris];
      const fileHash = { ...state.fileHash };
      action.payload.forEach(f => {
        // check to see if the file exists in the sales array
        const sdIdx = salesDocumentsUris.indexOf(f.oldName);
        if (sdIdx > -1) {
          // if it does, replace it with the new name
          salesDocumentsUris[sdIdx] = f.newName;
        }

        // delete the entry from the fileHash
        delete fileHash[f.oldName];
      });

      state.fileHash = fileHash;
      state.salesDocumentsUris = salesDocumentsUris;
    },
    removeFile: {
      prepare: (uri: string) => ({ payload: { uri } }),
      reducer: (state, action: PayloadAction<{ uri: string }>) => {
        state.fileHash = omit(state.fileHash, action.payload.uri);
        state.salesDocumentsUris = state.salesDocumentsUris.filter(x => x !== action.payload.uri);
      }
    },
    renameFile: (state, action: PayloadAction<IBlobRenameRequest>) => {},
    setFiles: {
      prepare: (files: IBlobFile[], ref?: IBlobReference) => ({ payload: { files, ref } }),
      reducer: (state, action: PayloadAction<{ files: IBlobFile[]; ref?: IBlobReference }>) => {
        state.fileHash = { ...state.fileHash, ...keyBy<IBlobFile>(action.payload.files, x => x.uri) };
      }
    },
    setSalesDocumentPermissions: (state, action: PayloadAction<ISalesDocumentPermissionUpdate>) => {},
    setSalesDocuments: (state, action: PayloadAction<IBlobFile[]>) => {
      state.salesDocumentsUris = uniqPrimativeMerge(
        state.salesDocumentsUris,
        action.payload.map(x => x.uri)
      );
    },
    toggleLoading: {
      prepare: (value: string) => ({ payload: { value } }),
      reducer: (state, action: PayloadAction<{ value: string }>) => {
        state.loading = state.loading.includes(action.payload.value)
          ? state.loading.filter(x => x !== action.payload.value)
          : [...state.loading, action.payload.value];
      }
    },
    uploadFailure: (state, action: PayloadAction<IUploadFileError>) => {
      state.uploads.active = state.uploads.active.filter(x => x.url !== action.payload.url);
      state.uploads.failed = [...state.uploads.failed, action.payload];
    },
    uploadProgress: (state, action: PayloadAction<{ url: string; amount: number }>) => {
      state.uploads.progress = {
        ...state.uploads.progress,
        [action.payload.url]: action.payload.amount
      };
    },
    uploadRequest: (state, action: PayloadAction<IUploadFile>) => {
      state.uploads.active = [...state.uploads.active, action.payload];
      state.uploads.failed = state.uploads.failed.filter(x => x.url !== action.payload.url);
      state.uploads.progress = {
        ...state.uploads.progress,
        [action.payload.url]: 0
      };
    },
    uploadSuccess: (state, action: PayloadAction<IUploadFile>) => {
      state.uploads.active = state.uploads.active.filter(x => x.url !== action.payload.url);
      state.uploads.completed = [
        ...state.uploads.completed.filter(x => x.url !== action.payload.url),
        { url: action.payload.url, name: action.payload.file.name, date: new Date() }
      ];
      state.uploads.failed = state.uploads.failed.filter(x => x.url !== action.payload.url);
      state.uploads.progress = omit(state.uploads.progress, action.payload.url);
    }
  },
  extraReducers: builder => {
    builder.addCase(thunks.uploadToAzure.fulfilled, (state, { meta }) => {
      const { containerType, file, folder } = meta.arg;
      const url = `${containerType}/${folder}/${file.name}`;

      state.uploads.active = state.uploads.active.filter(x => x.url !== url);
      state.uploads.failed = state.uploads.failed.filter(x => x.url !== url);
      state.uploads.progress = omit(state.uploads.progress, url);
    });
    builder.addCase(thunks.uploadToAzure.rejected, (state, { error, meta }) => {
      const { containerType, file, folder } = meta.arg;
      const url = `${containerType}/${folder}/${file.name}`;

      state.uploads.active = state.uploads.active.filter(x => x.url !== url);
      //state.uploads.failed = [...state.uploads.failed, action.payload];

      console.log('error', error);
    });
  }
});

export const { actions: filesActionCreators, reducer: filesReducer } = filesSlice;
